import { useMemo } from "react"
import { Link } from "react-router"
import { motion } from "motion/react"
import { ChevronRight, FileCheck02 } from "@untitledui/icons"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates } from "@/lib/api/use-estimates"
import { StatusBadge } from "@/components/estimates/status-badge"
import { formatMoney } from "@/lib/service-lane/baseline-math"
import type { ApiEstimate } from "@/lib/api/types"
import { estimateCustomerName, vehicleDisplayName } from "@/lib/api/types"

// The service advisor's own estimates — a full, mobile-first list of what
// they've written and where each stands. The dashboard shows only a recent-5
// glance; this is the "see everything I've done" view they asked for.
export default function MyEstimatesPage() {
  const { user } = useAuth()
  const { activeTeamId } = useOrganization()
  const teamId = activeTeamId
  const { data: all = [], isLoading } = useEstimates(teamId ?? null)
  const meId = user?.id ? Number(user.id) : null

  const mine = useMemo(
    () =>
      all
        .filter(
          (e) =>
            e.isActive !== false && meId != null && e.createdBy?.id === meId,
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [all, meId],
  )

  const total = mine.reduce((s, e) => s + (e.avg || 0), 0)
  const openCount = mine.filter(
    (e) => e.status !== "Completed" && e.status !== "Declined",
  ).length

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          {/* Same entrance as the rest of the CRM surfaces (#65). */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-2xl px-4 md:px-8 py-6 md:py-10"
          >
            <header className="mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
                My estimates
              </h1>
              <p className="text-sm text-zinc-400 mt-0.5">
                Everything you've written, newest first.
              </p>
            </header>

            <div className="grid grid-cols-3 gap-2.5 mb-4">
              <Stat label="Total" value={String(mine.length)} />
              <Stat label="Open" value={String(openCount)} />
              <Stat label="Identified" value={formatMoney(total)} />
            </div>

            {isLoading ? (
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-xl bg-white border border-zinc-200/70" />
                ))}
              </div>
            ) : mine.length === 0 ? (
              <Empty />
            ) : (
              <div className="space-y-2">
                {mine.map((e) => (
                  <Row key={e.id} e={e} />
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white border border-zinc-200/70 px-3 py-2.5">
      <p className="text-[16px] font-bold text-zinc-900 tabular-nums leading-none">{value}</p>
      <p className="text-[10.5px] text-zinc-500 mt-1 leading-tight">{label}</p>
    </div>
  )
}

function Row({ e }: { e: ApiEstimate }) {
  const date = new Date(e.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
  return (
    <Link
      to={`/estimates/${e.id}`}
      className="flex items-center gap-3 rounded-xl border border-zinc-200/70 bg-white px-4 py-3 hover:shadow-sm transition-shadow group"
    >
      <div className="min-w-0 flex-1">
        {/* An advisor thinks in people ("did Marcus book?"), not VINs — the
            customer leads, the vehicle supports. */}
        <p className="text-[14px] font-semibold text-zinc-900 truncate">
          {estimateCustomerName(e)}
        </p>
        <p className="text-[12px] text-zinc-400 truncate">
          {vehicleDisplayName(e.vehicle)} · {date}
        </p>
      </div>
      <p className="text-[14px] font-bold text-zinc-800 tabular-nums shrink-0">
        {formatMoney(e.avg)}
      </p>
      {/* Always visible — this page's primary device is the SA's phone, and
          the old `xs:` gate (not a real breakpoint here) hid the status
          exactly there. */}
      <StatusBadge status={e.status} className="shrink-0" />
      <ChevronRight
        className="size-4 text-zinc-300 group-hover:text-zinc-500 shrink-0"
        aria-hidden
      />
    </Link>
  )
}

function Empty() {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
      <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
        <FileCheck02 className="size-5 text-zinc-400" aria-hidden />
      </div>
      <p className="text-[14px] font-semibold text-zinc-900">No estimates yet</p>
      <p className="text-[13px] text-zinc-500 mt-1">
        Estimates you write will show up here.
      </p>
      <Link
        to="/estimates/quick?return=/my-estimates"
        className="inline-flex items-center gap-1.5 mt-4 h-10 px-4 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold hover:bg-black transition-colors"
      >
        Write an estimate
      </Link>
    </div>
  )
}
